import * as React from 'react';
import { useIntl } from 'react-intl';

import * as Styled from './icon-button.styles';
import type { IconButtonProps } from './icon-button.types';

export const IconButton = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  IconButtonProps
>(
  (
    {
      className,
      disabled,
      href,
      icon: Icon,
      innerRef,
      loading,
      onClick,
      size = 'medium',
      sizeConfined,
      sizeWide,
      to,
      type,
      ...props
    },
    forwardedRef
  ) => {
    const intl = useIntl();

    const iconSize = size === 'small' ? 18 : 24;

    const combinedRef = React.useMemo(() => {
      if (forwardedRef) {
        return typeof forwardedRef === 'function'
          ? { current: null, set: forwardedRef }
          : forwardedRef;
      }
      return innerRef;
    }, [forwardedRef, innerRef]);

    const commonProps = {
      $size: size,
      $sizeConfined: sizeConfined,
      $sizeWide: sizeWide,
      'aria-busy': loading,
      'aria-disabled': disabled || loading,
      'aria-label': loading
        ? intl.formatMessage({
            id: 'XDS_A11Y_LOADING',
            defaultMessage: 'Loading',
          })
        : undefined,
      'data-loading': loading,
      'data-xds': 'IconButton',
      className,
      disabled: disabled || loading,
      onClick,
    };

    const buttonContent = (
      <>
        {!loading && Icon && (
          <Styled.IconContainer $iconSize={iconSize}>
            <Icon width={iconSize} height={iconSize} aria-hidden="true" />
          </Styled.IconContainer>
        )}
        {loading && (
          <Styled.Loading
            $size={size}
            $sizeConfined={sizeConfined}
            $sizeWide={sizeWide}
            aria-hidden="true"
          />
        )}
      </>
    );

    if (to || href) {
      return (
        <Styled.RouterIconButton
          {...(to && { to })}
          {...(href && { href })}
          {...commonProps}
          ref={combinedRef as React.Ref<HTMLAnchorElement>}
          {...props}
        >
          {buttonContent}
        </Styled.RouterIconButton>
      );
    }

    return (
      <Styled.IconButton
        type={type}
        {...commonProps}
        ref={combinedRef as React.Ref<HTMLButtonElement>}
        {...props}
      >
        {buttonContent}
      </Styled.IconButton>
    );
  }
);

IconButton.displayName = 'IconButton';
